import React, { ReactElement, useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';
import Button from '../../../buttons/Button';
import { calculateAmountToPick, generatePickID, mergeClassNames } from '../../../../utils';
import { getTotalPickedQuantity } from '../../../../utils/pickHelpers';

import { updatePickTourItemsAndExit, updatePickTour } from '../../../../redux/actions/activePickTour.actions';

import { UpdateType, UpdateStatus } from '../../../../redux/actions/activePickTour.types';

import styles from './ExitPickTour.css';
import useInactivePickTourKickout from '../../../../hooks/useInactivePickTourKickout';
import ErrorFallback from '../../../shared/ErrorFallback';
import { getConfigAuthValuesFromState, isEditOrderEnabled } from '../../../../redux/selectors';
import { LANGUAGE_SPANISH, ORDER_TYPE_COMMERCIAL } from '../../../../constants';
import { PaginationData, PickTour, OrderType } from '../../../../types';
import { RootState } from '../../../../redux/reducers/rootReducer';
import { APIPickTourListItem } from '../../../../redux/sagas/types';

type Props = {
  orderType?: OrderType;
  determinePickTourStatus: (updateType: UpdateType) => UpdateStatus;
  currentLanguage: string;
  pickTour: PickTour;
  paginationData: PaginationData;
  returnToOrderList: () => void;
};

type PickTourMessageProps = {
  currentLanguage: string;
  orderType?: OrderType;
  t: (key: string) => string;
  translationRoot: string;
  editOrderEnabled: boolean;
};

const getPickTourMessage = ({
  currentLanguage,
  orderType,
  t,
  translationRoot,
  editOrderEnabled,
}: PickTourMessageProps): ReactElement => {
  // Show new message only for commercial orders when editOrderEnabled is true
  if (orderType === ORDER_TYPE_COMMERCIAL && editOrderEnabled) {
    return (
      <>
        <p className={styles.notification} data-testid="exit-pick-tour-notification">
          <span className={styles.attn} data-testid="exit-pick-tour-attn">
            {t(`${translationRoot}.ATTN`)}:
          </span>{' '}
          {t(`${translationRoot}.EXIT_PICK_TOUR_NOTIFICATION`)}
        </p>
        <p className={styles.confirmation} data-testid="exit-pick-tour-confirmation">
          {t(`${translationRoot}.EXIT_PICK_TOUR_CONFIRMATION`)}
        </p>
      </>
    );
  }

  // Default message for backward compatibility
  return (
    <>
      <h1
        className={mergeClassNames(styles.h1, currentLanguage === LANGUAGE_SPANISH && styles.h1Spanish)}
        data-testid="exit-pick-tour-heading"
      >
        {t(`${translationRoot}.EXIT_PICK_TOUR`)}
      </h1>
      <p className={styles.p} data-testid="exit-pick-tour-confirmation">
        {t(`${translationRoot}.EXIT_PICK_TOUR_CONFIRMATION`)}
      </p>
    </>
  );
};

const ExitPickTour = ({
  determinePickTourStatus,
  currentLanguage,
  orderType,
  pickTour,
  paginationData,
  returnToOrderList
}: Props): ReactElement => {
  const configAuthValues = useSelector(getConfigAuthValuesFromState);
  const pickTourList = useSelector((state: RootState) => state.activePickTour?.data?.pickTourList || []);
  const editOrderEnabled = useSelector(isEditOrderEnabled);
  const { t } = useTranslation();
  const translationRoot = 'PICK_TOUR_COMPLETE_OR_EXIT';
  const INACTIVITY_TIMEOUT = 1000 * 60 * (configAuthValues?.INACTIVITY_TIMEOUT || 10);
  const dispatch = useDispatch();
  const [showNavigationFailure, setShowNavigationFailure] = useState(false);
  const navigationTimerRef = useRef<number | null>(null);

  useInactivePickTourKickout(INACTIVITY_TIMEOUT);

  const handleReturnClick = () => {
    // Clear any existing timer first
    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
    }

    returnToOrderList();
    // If navigation doesn't happen within 3 seconds, show error fallback
    navigationTimerRef.current = window.setTimeout(() => {
      setShowNavigationFailure(true);
      navigationTimerRef.current = null;
    }, 3000);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (navigationTimerRef.current) {
        clearTimeout(navigationTimerRef.current);
      }
    };
  }, []);

  if (showNavigationFailure) {
    return <ErrorFallback onReturnClick={handleReturnClick} />;
  }

  const handleExitPickTourCommercial = () => {
    // Collect all item updates that need to be processed
    const itemUpdates = pickTour.picks
      .map((pick, index) => {
        const pickID = generatePickID(pick);
        const persistedPickItem = pickTourList.find(
          (item: APIPickTourListItem) =>
            `${item.orderType}:${item.orderID}:${item.pickTourItemDetailLineNumber}` === pickID
        );

        // Calculate new picked quantity from current session (from locations)
        const newPicksFromLocations = getTotalPickedQuantity(pick.locations);

        // Calculate total picked quantity (previous + new) for validation
        const totalPickedFromLocations = (persistedPickItem?.pickedItemQuantity || 0) + newPicksFromLocations;

        if (
          pick.isEligibleForPicking &&
          !pick.isSkipped &&
          newPicksFromLocations > 0 &&
          totalPickedFromLocations < calculateAmountToPick(pick)
        ) {
          return {
            pickID,
            pickedQuantity: newPicksFromLocations, // Send DELTA (new picks only) to API
            skipReason: null,
            isSkipped: false,
            locationIndex: pick.locationIndex,
            nextPick: pick,
            isLastPick: index === pickTour.picks.length - 1,
          };
        }
        return null;
      })
      .filter((update): update is NonNullable<typeof update> => update !== null);

    // Dispatch a single action that batches all updates and then exits
    dispatch(
      updatePickTourItemsAndExit({
        itemUpdates,
        paginationData,
        updateStatus: determinePickTourStatus('exit'),
      })
    );
  };

  const handleExitPickTour = () => {
    dispatch(updatePickTour({ updateType: 'exit', updateStatus: determinePickTourStatus('exit') }));
  };

  const onExitClick = orderType === ORDER_TYPE_COMMERCIAL ? handleExitPickTourCommercial : handleExitPickTour;

  return (
    <section className={styles.section} data-testid="exit-pick-tour-section">
      {getPickTourMessage({ currentLanguage, orderType, t, translationRoot, editOrderEnabled })}
      {
        !isEditOrderEnabled && (
          <>
            <h1 className={mergeClassNames(styles.h1, currentLanguage === LANGUAGE_SPANISH && styles.h1Spanish)}>
              {t(`${translationRoot}.EXIT_PICK_TOUR`)}
            </h1>
            <p className={styles.p}>{t(`${translationRoot}.EXIT_PICK_TOUR_CONFIRMATION`)}</p>
          </>
        )
      }
      <Button
        className={styles.button}
        onClick={onExitClick}
        data-cy="pick-tour-confirm-exit-button"
        data-testid="exit-pick-tour-button"
      >
        {t(`${translationRoot}.EXIT_PICK_TOUR`)}
      </Button>
    </section>
  );
};

export default ExitPickTour;
